import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { en } from '@/lib/i18n/en';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Top-level React error boundary. Logs to console in dev; Wave B will
 * swap this for a Sentry transport.
 *
 * The fallback UI is deliberately minimal - we keep it safe to render
 * even if the rest of the tree is in a bad state. i18n is read from the
 * static `en` bundle directly to avoid a context lookup mid-crash.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
          <div className="rounded-full bg-danger/15 p-4 text-danger">
            <AlertTriangle className="h-8 w-8" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-text-primary">
            {en.error.boundaryTitle}
          </h1>
          <p className="text-sm text-text-secondary">{en.error.boundaryDescription}</p>
          <Button onClick={this.handleReload} variant="default">
            {en.error.reload}
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
